import React, { useEffect } from 'react'
import { Text, Box, HStack } from '@chakra-ui/react'
import { FormikErrors, FormikTouched } from 'formik'
import { LabeledSelect, LabeledInput, Button } from '@apex/component-library'
import { RecipientTypes, DeliveryMethodTypes } from '../../containers/CheckTransfers/Types'
import { AddressForm } from '../AddressForm'
import { COLUMN_WIDTH, MAX_COLUMN_WIDTH } from '../../utils'

export interface CheckRecipientFormikValues {
  sendTo: RecipientTypes
  beneficiaryName: string
  recipientAddress: string
  recipientAddressLine2: string
  recipientCity: string
  recipientState: string
  recipientZip: string
  recipientCountry: string
  deliveryMethod?: DeliveryMethodTypes
}

interface CheckRecipientFormProps {
  formikValues: CheckRecipientFormikValues
  formikBlur: (e: React.FocusEvent<any>) => void
  formikTouched: FormikTouched<CheckRecipientFormikValues>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  setTouched: (fields: object, validate: boolean) => void
  errors: FormikErrors<CheckRecipientFormikValues>
  accountNames?: string
  accountNumber?: string
  countryList: { value: string }[]
  countryListLoading: boolean
  countryListFailedMessage?: string
  getCountryList: () => void
  hasOvernightToApexPerm: boolean
}

const CheckRecipientForm = ({
  formikValues,
  formikBlur,
  formikTouched,
  setFieldValue,
  setTouched,
  errors,
  accountNames,
  accountNumber,
  countryList,
  countryListLoading,
  countryListFailedMessage,
  getCountryList,
  hasOvernightToApexPerm
}: CheckRecipientFormProps) => {
  const {
    sendTo,
    beneficiaryName,
    recipientAddress,
    recipientAddressLine2,
    recipientCity,
    recipientState,
    recipientZip,
    recipientCountry,
    deliveryMethod
  } = formikValues

  useEffect(() => {
    if (formikValues.sendTo !== 'ACCOUNT-HOLDER') {
      getCountryList()
    }
    setFieldValue('beneficiaryName', '')
    setFieldValue('recipientAddress', '')
    setFieldValue('recipientAddressLine2', '')
    setFieldValue('recipientCity', '')
    setFieldValue('recipientState', '')
    setFieldValue('recipientZip', '')
    setFieldValue('recipientCountry', '')
    setTouched({}, true)
  }, [getCountryList, formikValues.sendTo, setFieldValue, setTouched])

  return (
    <>
      <Text as='h3' textStyle='h3'>
        Recipient Information
      </Text>
      <HStack spacing={4}>
        <Box width={COLUMN_WIDTH} maxW={MAX_COLUMN_WIDTH} height='5em'>
          <LabeledSelect
            label='Send To'
            isRequired
            name='sendTo'
            aria-label='send to'
            onBlur={formikBlur}
            value={sendTo}
            onChange={(e) => {
              setFieldValue('sendTo', e.target.value)
            }}
            isInvalid={formikTouched.sendTo && !!errors?.sendTo}
            errorMessage={errors?.sendTo}
          >
            <option value='ACCOUNT-HOLDER'>Account Holder</option>
            <option value='ALTERNATE-ADDRESS'>Alternate Address</option>
            <option value='THIRD-PARTY'>Third Party</option>
          </LabeledSelect>
        </Box>
        {formikValues.sendTo === 'ACCOUNT-HOLDER' && (
          <Box paddingTop={6}>
            <Button
              data-testid='view-address-btn'
              onClick={() => window.open(`/accounts/#/search/${accountNumber}`, '_blank')}
              fontWeight='400'
              variant='link'
            >
              View Address
            </Button>
          </Box>
        )}
      </HStack>
      <Box height='5em'>
        <LabeledInput
          label='Beneficiary Name'
          name='beneficiaryName'
          isRequired
          onBlur={formikBlur}
          value={sendTo !== 'THIRD-PARTY' ? accountNames : beneficiaryName}
          isReadOnly={sendTo !== 'THIRD-PARTY'}
          onChange={(e) => {
            setFieldValue('beneficiaryName', e.target.value)
          }}
          errorMessage={errors?.beneficiaryName}
          isInvalid={formikTouched.beneficiaryName && !!errors?.beneficiaryName}
        />
      </Box>
      {formikValues.sendTo !== 'ACCOUNT-HOLDER' && (
        <AddressForm
          address={{
            label: 'recipient address',
            name: 'recipientAddress',
            value: recipientAddress,
            errors: errors?.recipientAddress,
            touched: !!formikTouched.recipientAddress
          }}
          addressLine2={{
            label: 'recipient address line 2',
            name: 'recipientAddressLine2',
            value: recipientAddressLine2,
            errors: errors?.recipientAddressLine2,
            touched: !!formikTouched.recipientAddressLine2
          }}
          city={{
            label: 'recipient city',
            name: 'recipientCity',
            value: recipientCity,
            errors: errors?.recipientCity,
            touched: !!formikTouched.recipientCity
          }}
          state={{
            label: 'recipient state',
            name: 'recipientState',
            value: recipientState,
            errors: errors?.recipientState,
            touched: !!formikTouched.recipientState
          }}
          zip={{
            label: 'recipient zip',
            name: 'recipientZip',
            value: recipientZip,
            errors: errors?.recipientZip,
            touched: !!formikTouched.recipientZip
          }}
          country={{
            label: 'recipient country',
            name: 'recipientCountry',
            value: recipientCountry,
            errors: errors?.recipientCountry,
            touched: !!formikTouched.recipientCountry
          }}
          handleBlur={formikBlur}
          setValue={setFieldValue}
          countryList={countryList}
          countryListLoading={countryListLoading}
          countryListFailedMessage={countryListFailedMessage}
        />
      )}
      <Box height='5em' width={COLUMN_WIDTH} maxW={MAX_COLUMN_WIDTH}>
        <LabeledSelect
          label='Delivery Method'
          aria-label='Delivery Method'
          isRequired
          name='deliveryMethod'
          onBlur={formikBlur}
          value={deliveryMethod}
          onChange={(e) => {
            setFieldValue('deliveryMethod', e.target.value)
          }}
          isInvalid={formikTouched.deliveryMethod && !!errors?.deliveryMethod}
          errorMessage={errors?.deliveryMethod}
        >
          <option></option>
          <option value='STANDARD'>Standard</option>
          <option value='OVERNIGHT'>Overnight</option>
          {hasOvernightToApexPerm && <option value='OVERNIGHT_TO_APEX'>Overnight to Apex</option>}
        </LabeledSelect>
      </Box>
    </>
  )
}

export default CheckRecipientForm
